import React, {Fragment, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {gql} from 'apollo-boost';
import {useQuery} from '@apollo/react-hooks';
import {useHistory, useParams} from 'react-router-dom';
import {Button} from '@blueprintjs/core';
import {Breadcrumbs, Loading} from 'worksource-ui';
import ShareFilesBar from 'components/ShareFilesBar';
import mixpanel from 'mixpanel-browser';

import QASearchBar from 'components/QASearchBar';
import AnalyticsActions from 'services/analytics/actions';
import {EventIdentifier} from 'services/analytics/constants';
import {showGQLError} from 'utils/toaster';
import HeaderBar from 'components/header-bar';
import withMainLayout from 'layouts/main/view';

const FETCH_GUIDE = gql`
  query GuidesShowPageFetch($clientId: ID!, $guideId: ID!) {
    clientFeatures(clientId: $clientId) {
      ragEnabled
    }

    guides(clientId: $clientId, id: $guideId) {
      name,
      pages(topLevel: true) {
        id,
        name,
        coverImageUrl,
        fallbackColor,
      },
    }
  }
`;

const GuidesShowPage = () => {
  ////
  // Hooks
  //
  const dispatch = useDispatch();
  const {clientId, guideId} = useParams();
  const history = useHistory();

  useEffect(() => {
    mixpanel.track('Open guide viewer', {clientId, guideId});
    dispatch(AnalyticsActions.trigger({
      clientId,
      guideId,
      identifier: EventIdentifier.GUIDE_OPEN,
    }));
  }, []);

  ////
  // GraphQL
  //
  const {loading: loadingFetch, data} = useQuery(FETCH_GUIDE, {
    variables: {clientId, guideId},
    onError: error => showGQLError({error}),
  });

  ////
  // Events
  //
  const onClickPage = pageId => () => {
    history.push(`/clients/${clientId}/guides/${guideId}/pages/${pageId}`);
  };

  const onFocusSearch = () => {
    history.push(`/clients/${clientId}/guides/${guideId}/search`);
  };

  ////
  // Render
  //
  if (loadingFetch || !data) {
    return (
      <Loading />
    );
  }

  const {guides: [guide], clientFeatures} = data;

  return (
    <Fragment>
      <HeaderBar>
        <Breadcrumbs>
          <Breadcrumbs.Breadcrumb
            href={`/clients/${clientId}/guides`}
            icon="book"
            text="Guides"
          />
          <Breadcrumbs.Breadcrumb
            current
            href={`/clients/${clientId}/guides/${guideId}`}
            text={guide.name}
          />
        </Breadcrumbs>
        <div className="spacer" />
        <Button
          icon="star"
          text="Favorites"
          onClick={() => {
            history.push(`/clients/${clientId}/guides/${guideId}/favorites`);
          }}
        />
      </HeaderBar>

      <ShareFilesBar />

      <div className="guides-show--container content-container">
        {clientFeatures?.ragEnabled && (
          <QASearchBar
            showHeader
            onFocus={onFocusSearch}
          />
        )}
        <div className="pages-show--header">Pages</div>
        <div className="guides-show--pages-grid">
          {guide.pages.map((page) => {
            return (
              <div
                key={page.id}
                className="guides-show--page"
                style={{
                  backgroundColor: page.fallbackColor,
                  backgroundImage: `url('${page.coverImageUrl}')`,
                }}
                onClick={onClickPage(page.id)}
              >
                <div className="guides-show--page-text">{page.name}</div>
              </div>
            );
          })}
        </div>
      </div>
    </Fragment>
  );
};

export default withMainLayout({viewerMenu: true})(GuidesShowPage);
